import { loginFailure, loginSuccess, registrationFailure, registrationSuccess } from '@comeon/analytics';
import reducerRegistry from '@comeon/reducer-registry';

import { WEB_EVENTS, sendAppBridgeEvent } from '~/components/app-bridge';
import { enableValidateSession, updateCredentials, validateSession } from '~/components/authentication/ducks';
import { register } from '~/components/authentication/playnow/steps/DGA/api';
import { GENERAL_ERROR, RESET_FLOW_ERROR_CODES, STATUS } from '~/components/authentication/playnow/steps/DGA/constants';
import { formSubmitStop } from '~/components/forms/ducks';
import { showNotification } from '~/components/notifications/ducks/notifications';

import * as AN from './actions';

const trackingErrMap = {
	ACC_LOCKED: 'ACC_LOCKED',
	MITID_USER_ABORTED: 'MITID_USER_ABORTED',
	MOBILE_PLAYER_SUSPEND_TIME_ACCOUNT: 'MOBILE_PLAYER_SUSPEND_TIME_ACCOUNT',
	MITID_UNEXPECTED_ERROR: 'MITID_UNEXPECTED_ERROR',
	MITID_CPR_MATCH_FAILED: 'MITID_CPR_MATCH_FAILED',
	MITID_NO_CTX: 'MITID_NO_CTX'
};

export const clearProvider = () => ({ type: AN.CLEAR });

export const errorProvider = payload => ({
	type: AN.ERROR,
	payload
});

export const updateSessionCriteria = payload => ({
	type: AN.SESSION_UPDATE,
	payload
});

const registrationTracking = ({ error, method, encryptedPID }) => {
	if (!error) {
		registrationSuccess({ method, encryptedPID, newEvent: true });
		sendAppBridgeEvent(WEB_EVENTS.ENCRYPTED_PID_UPDATED_FOR_EV, [{ encryptedPlayerId: encryptedPID }]);
	} else {
		registrationFailure({ method, error, newEvent: true });
	}
};

const loginTracking = ({ error, method }) => {
	if (!error) {
		loginSuccess({ method, newEvent: true });
	} else {
		loginFailure({ method, error, newEvent: true });
	}
};

export const tracking =
	({ error: errorCode = '', trackRegistration = false, provider }) =>
	(_, getState) => {
		const { account, authentication, translations } = getState();

		if (!provider) {
			return;
		}

		const error =
			!!errorCode && (translations[trackingErrMap[errorCode] ?? errorCode] || translations[GENERAL_ERROR]);
		const method = account.isRegistration ? `one-button-flow - ${provider}` : provider;

		if (account.isRegistration) {
			if (trackRegistration) {
				registrationTracking({ error, method, encryptedPID: account?.id });
			}

			return;
		}

		if (authentication.authenticated || error) {
			loginTracking({ error, method });
		}
	};

export const registration = payload => async (dispatch, getState) => {
	const encryptedPlayerId = getState().account.id;
	const { isRegistration } = getState().providers?.sessionCriteria ?? {};
	const formattedPayload = { ...(payload ?? {}), encryptedPlayerId };

	Object.keys(formattedPayload).forEach(key => {
		if (formattedPayload[key] && typeof formattedPayload[key] === 'string') {
			formattedPayload[key] = formattedPayload[key].trim();
		}
	});

	try {
		const response = await register(formattedPayload);

		if (response?.status === 'SUCCESS') {
			const {
				showWelcomePageDetails = false,
				depositLimitCountryCode,
				...sessionCriteria
			} = response?.verificationMap ?? {};

			dispatch(updateCredentials({ deviseSession: payload.deviseSession }));
			dispatch(
				updateSessionCriteria({
					...sessionCriteria,
					depositLimitCountryCode,
					showWelcomePageDetails,
					showRegistrationForm: true,
					isRegistration,
					showRegistrationFlow:
						isRegistration || sessionCriteria.showDepositLimits || sessionCriteria.showRegistrationForm
				})
			);

			if (Object.keys(sessionCriteria).every(k => !sessionCriteria[k])) {
				dispatch(enableValidateSession());
				await dispatch(validateSession({ skipAnalytics: true }, true));
				dispatch(tracking({}));
			}

			dispatch({ type: AN.UPDATE_DETAILS, payload: { success: true } });
		} else {
			dispatch(formSubmitStop());

			if (RESET_FLOW_ERROR_CODES.indexOf(response.errorTextKey) !== -1) {
				dispatch(tracking({ error: 'SESSION_NOT_FOUND' }));
				return dispatch(errorProvider('SESSION_NOT_FOUND'));
			}

			dispatch(
				showNotification({
					messageKey: response.errorTextKey ?? GENERAL_ERROR,
					color: 'red',
					autoHide: true,
					openInSidebar: true
				})
			);
		}
	} catch (err) {
		// eslint-disable-next-line no-console
		console.log(err);

		dispatch(formSubmitStop());
		dispatch(tracking({ error: GENERAL_ERROR }));
		dispatch(errorProvider(GENERAL_ERROR));
	}
};

const initialState = {
	error: null,
	verification: {
		status: STATUS.START,
		step: null,
		data: null
	},
	sessionCriteria: null,
	details: null
};

const reducer = (state = initialState, { type, payload }) => {
	switch (type) {
		case AN.CLEAR:
			return initialState;
		case AN.ERROR:
			return {
				...state,
				error: payload,
				verification: {
					...state.verification,
					data: {
						...state.verification?.data,
						url: null
					}
				}
			};
		case AN.SESSION_UPDATE:
			return {
				...state,
				sessionCriteria: payload
			};
		case AN.UPDATE_DETAILS:
			return {
				...state,
				details: payload
			};
		default:
			return state;
	}
};

reducerRegistry.register('providers', reducer);
export default reducer;
